// src/modules/auth/components/LoginForm.tsx
import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  styled,
  Icon,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Link,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import logo from "../../../assets/logo/text-logo.png";
import background from "../../../assets/auth/login.png";

// Styled components
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    lineHeight: 1.5,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: "12px",
  fontSize: "0.875rem",
  textTransform: "none",
  fontWeight: 600,
}));

interface LoginFormProps {
  onSubmit: (values: { email: string; password: string }) => Promise<void>;
  isLoading?: boolean;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  return (
    <Box sx={{ display: "flex", height: "100%", width: "100%" }}>
      {/* Left Side - Login Form */}
      <Box
        sx={{
          flex: { xs: "1 1 100%", lg: "1 1 50%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: { xs: 4, sm: 6, lg: 8 },
          py: { xs: 6, lg: 0 },
          position: "relative",
          zIndex: 1,
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "440px", mx: "auto" }}>
          <Box sx={{ mb: 6 }}>
            <Box
              sx={{
                width: "80%",
                // maxWidth: "200px",
                mb: 4,
              }}
            >
              <img
                src={logo}
                alt="VenFlows"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.75rem", md: "2rem" },
              }}
            >
              Sign in to your account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back! Please enter your details.
            </Typography>
          </Box>

          <Box component="form" onSubmit={formik.handleSubmit} noValidate>
            <Box sx={{ mb: 3 }}>
              <Typography
                component="label"
                htmlFor="email"
                sx={{
                  display: "block",
                  mb: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                Email address
              </Typography>
              <StyledTextField
                fullWidth
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={isLoading}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                component="label"
                htmlFor="password"
                sx={{
                  display: "block",
                  mb: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                Password
              </Typography>
              <StyledTextField
                fullWidth
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        <Icon>
                          {showPassword ? "visibility_off" : "visibility"}
                        </Icon>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: "#06B6D4",
                      "&.Mui-checked": {
                        color: "#06B6D4",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    Remember me
                  </Typography>
                }
              />
              <Link
                href="#"
                variant="body2"
                sx={{
                  background:
                    "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textDecoration: "none",
                  fontWeight: 600,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  },
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <StyledButton
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                color: "white",
                mb: 2,
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%)",
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign in"
              )}
            </StyledButton>

            <Box sx={{ my: 3 }}>
              <Divider>
                <Typography
                  variant="body2"
                  sx={{
                    px: 2,
                    color: "text.secondary",
                    bgcolor: "background.paper",
                  }}
                >
                  Or
                </Typography>
              </Divider>
            </Box>

            <StyledButton
              fullWidth
              variant="outlined"
              onClick={() => navigate("/auth/cashier-login")}
              sx={{
                borderColor: "#06B6D4",
                color: "#06B6D4",
                "&:hover": {
                  borderColor: "#0891B2",
                  backgroundColor: "rgba(6, 182, 212, 0.04)",
                },
                mb: 3
              }}
            >
              Login as Cashier
            </StyledButton>

            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary' }}>
                Don't have an account?{' '}
                <RouterLink 
                  to="/auth/register"
                  style={{
                    background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textDecoration: "none",
                    fontWeight: 500
                  }}
                >
                  Sign up
                </RouterLink>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right Side - Background Image */}
      <Box
        sx={{
          flex: "1 1 50%",
          position: "relative",
          display: { xs: "none", lg: "block" },
          background: "linear-gradient(to top, #00333F 0%, #0891B2 100%)",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={background}
          alt="Login background"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            userSelect: "none",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            // background: "rgba(0, 0, 0, 0.2)",
            // backdropFilter: "blur(1px)",
            userSelect: "none",
            pointerEvents: "none",
          }}
        />
      </Box>
    </Box>
  );
};
